
// API Key validation functions for different platforms

export interface ValidationResult {
  isValid: boolean;
  message: string;
}

export const validateApiKey = async (platform: string, credentials: Record<string, string>): Promise<ValidationResult> => {
  if (platform === 'lutra-webhook') {
    const { webhookUrl, webhookToken } = credentials;
    
    if (!webhookUrl || !webhookToken) {
      return {
        isValid: false,
        message: 'Both webhook URL and token are required'
      };
    }
    
    // Validate webhook URL format
    if (!webhookUrl.startsWith('https://')) {
      return {
        isValid: false,
        message: 'Webhook URL must start with https://'
      };
    }
    
    try {
      new URL(webhookUrl);
    } catch (error) {
      return {
        isValid: false,
        message: 'Invalid webhook URL format'
      };
    }
    
    // Validate webhook token format
    if (webhookToken.length < 10) {
      return {
        isValid: false,
        message: 'Webhook token must be at least 10 characters long'
      };
    }
    
    if (webhookToken.toLowerCase().includes('error') || webhookToken.toLowerCase().includes('invalid')) {
      return {
        isValid: false,
        message: 'Invalid webhook token format'
      };
    }
    
    return {
      isValid: true,
      message: 'Lutra webhook credentials validated successfully'
    };
  }
  
  // Generic validation for other platforms
  const apiKey = credentials.api_key || credentials.apiKey || Object.values(credentials)[0];
  
  if (!apiKey || apiKey.trim() === '') {
    return {
      isValid: false,
      message: 'API key is required'
    };
  }
  
  if (apiKey.length < 10) {
    return {
      isValid: false,
      message: 'API key appears to be too short'
    };
  }
  
  return {
    isValid: true,
    message: 'API key format appears valid'
  };
};

export const testApiConnection = async (platform: string, credentials: Record<string, string>): Promise<ValidationResult> => {
  // For lutra-webhook, we can't actually test the connection due to CORS
  // So we'll just validate the format
  if (platform === 'lutra-webhook') {
    return await validateApiKey(platform, credentials);
  }
  
  // For other platforms, perform basic validation
  // In a real implementation, you would make actual API calls here
  return await validateApiKey(platform, credentials);
};
